import {IWedChildrenElt, IWedDisplayElt, IWedletModel, WED, WedletModelBase} from "/@back@/edit/wed/wedCore.js";
import {ICharsWedlet, IChildWedlet, IParentWedlet, IVirtualisableWedlet, OFindWedletOptions, OVisitWedletOptions, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {IJmlNode, IJmlObj, IJmlSet, IJmlSubSet, JML, JmlSubSetIterator} from "/@lib@/commons/xml/jml.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {applyXmlMsgOnElt, IXmlMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {ENodeType} from "/@lib@/commons/xml/dom.js";
import {FetchJml} from "/@lib@/edit/docHolder.js";
import {LANG} from "/@lib@/commons/lang.js";

/**
 * Wedlet auto-porté indépendant du dom HTML (!= wedletSingleElt).
 * L'implémentation de base ne fait que maintenir une structure de wedlets en phase avec
 * la source xml.
 * Aucun usage fonctionnel direct, utilisé par d'autres types de wedlets : spte...
 *
 * La configuration du wedlet peut contenir 0 à n <wed:children/> pour définir les wedlets descendants.
 * Le matching est strict : si un noeud ou att ne matche aucun <wed:children/>, aucun wedlet fils n'est inséré.
 */
export class OffscreenModel extends WedletModelBase {

	/** Liste des childrenElt triés selon l'ordre de priorité de matching. */
	childrenElts: IWedChildrenElt[];

	initModel(cnf: Element): void {
		this.config = cnf;
		this.childrenElts = WEDLET.extractChildrenNodes(cnf);
		this.childrenElts.sort(WED.SORT_ChildrenElts);
	}

	createWedlet(parent: IParentWedlet, displayContext?: IWedDisplayElt): IChildWedlet | null {
		if (this.nodeType === ENodeType.element) return new OffscreenEltWedlet(this, parent);
		return new OffscreenStrWedlet(this, parent);
	}
}

WED.registerWedletModel("Offscreen", OffscreenModel);


export class OffscreenEltWedlet implements IParentWedlet, IChildWedlet {
	model: OffscreenModel;
	wedParent: IParentWedlet;

	xaPart: number | string;

	get wedAnchor(): IXAddr {return XA.append(this.wedParent.wedAnchor, this.xaPart)}

	get wedMgr(): WedMgr {return this.wedParent.wedMgr};

	atts: Dict<ICharsWedlet>;

	children: IChildWedlet[];

	constructor(model: OffscreenModel, wedParent: IParentWedlet) {
		this.model = model;
		this.wedParent = wedParent;
		this.atts = {};
		this.children = [];
	}

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.xaPart = xaOffset;
		if (typeof node === 'string') throw Error("StandaloneEltWedlet not bindable on text node.");
		const wedMgr = this.wedMgr;
		for (const k in node) {
			const v = node[k];
			if (v.length > 0) this._setAtt(wedMgr, k, v);
		}
		const it = new JmlSubSetIterator(children);
		let promises: Promise<any>[];
		while (it.next()) {
			if (typeof it.currentNode === 'number') {
				const xa = XA.append(this.wedAnchor, it.currentIdx);
				if (wedMgr.docHolder) {
					const ct = wedMgr.getContent(xa);
					it.currentNode = ct[0] as IJmlNode;
					it.currentChildren = ct[1] as IJmlSubSet;
				} else {
					if (!promises) promises = [];
					promises.push(wedMgr.fetchContent(xa).then((jml: FetchJml) => {
						return this._setChild(wedMgr, XA.last(jml.xa) as number, jml.result[0] as IJmlNode, jml.result[1] as IJmlSubSet);
					}));
					continue;
				}
			}
			const p = this._setChild(wedMgr, it.currentIdx, it.currentNode, it.currentChildren);
			if (p) {
				if (!promises) promises = [];
				else promises.push(p);
			}
		}
		if (promises) return Promise.all(promises) as any as Promise<void>;
	}

	protected _setAtt(wedMgr: WedMgr, name: string, val: string) {
		const childrenElt = WEDLET.findChildrenEltForAttr(this.model.childrenElts, name);
		if (childrenElt) {
			const model = wedMgr.wedModel.findModelForAttr(name, val, childrenElt.wedModesAtts, childrenElt.wedVariants, this, childrenElt.wedSelector, childrenElt.wedPreferedModels, childrenElt.wedParams);
			if (model) {
				const w = this.atts[name] = model.createWedlet(this) as ICharsWedlet;
				if (w) w.bindWithAttr(name, val);
			}
		}
	}

	protected _setChild(wedMgr: WedMgr, idx: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		const childrenElt = WEDLET.findChildrenEltForNodeStrict(this.model.childrenElts, node);
		if (childrenElt) {
			//(it.currentIdx, it.currentNode, it.currentChildren);
			const model = wedMgr.wedModel.findModelForNode(node, childrenElt.wedModesNodes, childrenElt.wedVariants, this, childrenElt.wedSelector, childrenElt.wedPreferedModels, childrenElt.wedParams);
			if (model) {
				const w = this.children[idx] = model.createWedlet(this) as IChildWedlet;
				if (w) return w.bindWithNode(idx, node, children);
			}
		}
	}

	deleteAttrNode(nameAttr: string): void {
		if (this.atts[nameAttr]) {
			const w = this.atts[nameAttr];
			if (w && ('onDelete' in w)) w.onDelete();
			this.atts[nameAttr] = undefined;
		}
	}

	deleteChildNodes(xaOffest: number, count: number): void {
		for (let i = xaOffest, m = i + count; i < m; i++) {
			const w = this.children[i];
			if (w && ('onDelete' in w)) w.onDelete();
		}
		this.children.splice(xaOffest, count);
		for (let i = xaOffest; i < this.children.length; i++) {
			const w = this.children[i];
			if (w) w.xaPart = (w.xaPart as number) - count;
		}
	}

	findWedletChild(xaPart: number | string, options?: OFindWedletOptions): IChildWedlet | IChildWedlet[] {
		if (typeof xaPart === 'number') {
			return this.children[xaPart];
		} else {
			return this.atts[xaPart];
		}
		return null;
	}

	insertAttrNode(nameAttr: string, value: string): void | Promise<void> {
		this._setAtt(this.wedMgr, nameAttr, value);
	}

	insertChildNode(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.children.splice(xaOffest, 0, null);
		for (let i = xaOffest + 1; i < this.children.length; i++) {
			const w = this.children[i];
			if (w) w.xaPart = (w.xaPart as number) + 1;
		}
		return this._setChild(this.wedMgr, xaOffest, node, children);
	}

	replaceChildBind(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		const curr = this.children[xaOffest];
		if (curr && ('onDelete' in curr)) curr.onDelete();
		return this._setChild(this.wedMgr, xaOffest, node, children);
	}

	isEmpty(): boolean {return false}

	isVirtual(): this is IVirtualisableWedlet {return false}

	onChildNodesInserted(): void {}

	onDelete(): void {
		for (const w of Object.values(this.atts)) if (w && ('onDelete' in w)) w.onDelete();
		for (const w of this.children) if (w && ('onDelete' in w)) w.onDelete();
	}

	visitWedletChildren(from: number, len: number, visitor: (this: void, wedlet: IChildWedlet) => ("stop" | void), options?: OVisitWedletOptions): "stop" | void {
		if (from === -1) for (const w of Object.values(this.atts)) if (w && visitor(w) === 'stop') return 'stop';
		for (let i = Math.max(0, from), s = Math.min(from + len, this.children.length); i < s; i++) {
			const w = this.children[i];
			if (w && visitor(w) === 'stop') return 'stop';
		}
	}

}

/**
 * Wedlet offscreen terminal pour un element mettant à dispo un DOM de cet élément.
 * Note : destiné à être surachargé, donc ce wedlet n'a pas de WedletModel déclaré.
 */
export class OffscreenEltLeafWedlet implements IChildWedlet {
	model: IWedletModel;
	wedParent: IParentWedlet;
	wedMgr: WedMgr;

	xaPart: number | string;

	get wedAnchor(): IXAddr {return XA.append(this.wedParent.wedAnchor, this.xaPart)}

	/** Contenu XML sous forme DOM. */
	content: Element;

	constructor(model: IWedletModel, wedParent: IParentWedlet) {
		this.model = model;
		this.wedParent = wedParent;
		this.wedMgr = wedParent.wedMgr;
	}

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.xaPart = xaOffset;
		this.wedMgr.doAfterBatch(() => {
			if (this.wedMgr.docHolder) {
				this.setContent(XA.findDomLast(this.wedAnchor, this.wedMgr.docHolder.getDocument()) as Element);
			} else if (JML.isWhole(children)) {
				this.setContent(JML.jmlToElt(node as IJmlObj, children));
			} else {
				//XXX doAfterBatch avec Promise ?
				this.fetchContent();
			}
		});
	}

	updateInDescendants(msg: IXmlMsg): void {
		if (!this.wedMgr.docHolder) applyXmlMsgOnElt(msg, WEDLET.getWedletDepth(this) + 1, this.content);
		this.setContent(this.content); //notif un chgt.
	}

	isEmpty(): boolean {return this.content != null}

	isVirtual(): this is IVirtualisableWedlet {return false}

	onDelete(): void {
		this.setContent(null);
	}

	/** A appeler à chaque changement, destiné à être surchargé. */
	protected setContent(content: Element) {
		this.content = content;
	}

	protected async fetchContent() {
		const [jmlFetch] = await this.wedMgr.docHolderAsync.fetchContent([new FetchJml(XA.append(this.wedAnchor, 0))]);
		this.setContent(JML.jmlToElt(jmlFetch.result[0] as IJmlObj, jmlFetch.result[1] as IJmlSet));
	}
}


/** Wedlet offscreen pour att, text, comment */
export class OffscreenStrWedlet implements ICharsWedlet {
	model: IWedletModel;
	wedAnchor: IXAddr;
	wedParent: IParentWedlet;
	xaPart: number | string;

	text: string;

	get wedMgr(): WedMgr {return this.wedParent.wedMgr};

	constructor(model: IWedletModel, wedParent: IParentWedlet) {
		this.model = model;
		this.wedParent = wedParent;
	}

	protected setText(txt: string) {
		this.text = txt;
	}

	bindWithAttr(nameAttr: string, value: string): void | Promise<void> {
		this.xaPart = nameAttr;
		this.setText(value);
	}

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.xaPart = xaOffset;
		this.setText(node as string);
	}

	deleteChars(from: number, len: number, msg: IXmlMsg): void {
		this.setText(LANG.stringDelete(this.text, from, len));
	}

	insertChars(from: number, chars: string, msg: IXmlMsg): void {
		this.setText(LANG.stringInsert(this.text, from, chars));
	}

	isEmpty(): boolean {return false}

	isVirtual(): this is IVirtualisableWedlet {return false}

	replaceChars(chars: string, msg: IXmlMsg): void {
		this.setText(chars);
	}

	onDelete(): void {
		this.setText(null);
	}
}